/**
 * previewRenderer.js
 * Handles rendering the logo on the sign preview and
 * manages the download functionality.
 */

class PreviewRenderer {
  constructor(options = {}) {
    // DOM Elements
    this.logoOverlay = document.getElementById('logo-overlay');
    this.previewContainer = document.getElementById('sign-preview-container');
    this.previewMessage = document.getElementById('preview-message');
    this.signBackgroundImage = document.getElementById('sign-background-image');
    this.downloadButtons = [
      document.getElementById('download-preview'),
      document.getElementById('download-preview-mobile')
    ].filter(Boolean); // Filter out null/undefined
    
    // Options with defaults
    this.options = {
      maxLogoWidth: options.maxLogoWidth || 50, // percentage of container width
      maxLogoHeight: options.maxLogoHeight || 50, // percentage of container height
      logoMargin: options.logoMargin || 5, // percentage margin from edges
      downloadFilename: options.downloadFilename || 'custom-sign-preview'
    };
    
    // State
    this.logoFile = null;
    this.currentSignType = '';
    this.logoElement = null;
    
    // Initialize
    this.init();
  }
  
  init() {
    // Set up download buttons
    this.downloadButtons.forEach(button => {
      if (button) {
        button.addEventListener('click', () => this.downloadPreview());
      }
    });
  }
  
  setLogo(file) {
    this.logoFile = file;
    
    // Remove existing logo if any
    if (this.logoElement) {
      this.logoOverlay.removeChild(this.logoElement);
      this.logoElement = null;
    }
    
    if (file) {
      // Create new image element
      this.logoElement = document.createElement('img');
      this.logoElement.onload = () => {
        this.logoOverlay.classList.add('logo-loaded');
        this.updatePreviewState(true);
      };
      
      // Read file and set as source
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoElement.src = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Add to overlay
      this.logoOverlay.appendChild(this.logoElement);
    } else {
      this.logoOverlay.classList.remove('logo-loaded');
      this.updatePreviewState(false);
    }
  }
  
  setSignType(signType) {
    this.currentSignType = signType;
    
    // If we have a logo, we can refresh the preview to adjust positioning
    if (this.logoElement) {
      // Wait for the background image to load
      this.signBackgroundImage.onload = () => {
        // May need to adjust logo position based on the sign type
        this.adjustLogoPosition();
      };
    }
  }
  
  adjustLogoPosition() {
    // This method can be extended to adjust logo position
    // based on sign type-specific requirements
  }
  
  updatePreviewState(hasLogo) {
    // Update preview message and download button state
    if (hasLogo) {
      this.previewMessage.classList.add('d-none');
      this.downloadButtons.forEach(button => {
        if (button) button.disabled = false;
      });
    } else {
      this.previewMessage.classList.remove('d-none');
      this.downloadButtons.forEach(button => {
        if (button) button.disabled = true;
      });
    }
  }
  
  downloadPreview() {
    if (!this.logoElement || !this.signBackgroundImage.complete) {
      return;
    }
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions based on sign background image
    canvas.width = this.signBackgroundImage.naturalWidth;
    canvas.height = this.signBackgroundImage.naturalHeight;
    
    // Draw the sign background
    ctx.drawImage(this.signBackgroundImage, 0, 0, canvas.width, canvas.height);
    
    // Calculate logo position and dimensions
    const logoWidth = this.logoElement.naturalWidth;
    const logoHeight = this.logoElement.naturalHeight;
    
    // Scale logo to fit within max width/height percentage
    const maxLogoWidthPx = canvas.width * (this.options.maxLogoWidth / 100);
    const maxLogoHeightPx = canvas.height * (this.options.maxLogoHeight / 100);
    
    let scaledWidth = logoWidth;
    let scaledHeight = logoHeight;
    
    // Scale down if either dimension is too large
    if (logoWidth > maxLogoWidthPx || logoHeight > maxLogoHeightPx) {
      const widthRatio = maxLogoWidthPx / logoWidth;
      const heightRatio = maxLogoHeightPx / logoHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      
      scaledWidth = logoWidth * ratio;
      scaledHeight = logoHeight * ratio;
    }
    
    // Center the logo on the canvas
    const logoX = (canvas.width - scaledWidth) / 2;
    const logoY = (canvas.height - scaledHeight) / 2;
    
    // Draw the logo
    ctx.drawImage(this.logoElement, logoX, logoY, scaledWidth, scaledHeight);
    
    // Create a download link
    const link = document.createElement('a');
    link.download = `${this.options.downloadFilename}-${this.currentSignType}.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// Export
window.PreviewRenderer = PreviewRenderer;